import { useState, useEffect } from 'react'
import { useForm, router } from '@inertiajs/react'
import { useTranslation } from 'react-i18next'
import { Target, Trash2 } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import type { ActivitySubtype, ActivityType, Goal, GoalPeriod } from '@/types'

interface BaseProps {
  existingGoal?: Goal
  period: GoalPeriod
  open: boolean
  onOpenChange: (open: boolean) => void
}

interface TypeProps extends BaseProps {
  activityType: ActivityType
  activitySubtype?: never
}

interface SubtypeProps extends BaseProps {
  activityType?: never
  activitySubtype: ActivitySubtype
}

type Props = TypeProps | SubtypeProps

interface GoalFormData {
  activity_type_id: number | null
  activity_subtype_id: number | null
  period: GoalPeriod
  target_minutes: number
}

function parseDuration(input: string): number | null {
  if (!input.trim()) return null

  // Try parsing as just a number (treat as hours)
  const justNumber = parseFloat(input)
  if (!isNaN(justNumber) && input.match(/^\d+(\.\d+)?$/)) {
    return Math.round(justNumber * 60)
  }

  // Try parsing formats like "2h 30m", "2h", "30m", "2:30"
  let totalMinutes = 0

  // Handle "H:MM" format
  const colonMatch = input.match(/^(\d+):(\d+)$/)
  if (colonMatch) {
    return parseInt(colonMatch[1]) * 60 + parseInt(colonMatch[2])
  }

  // Handle "Xh Ym" format
  const hoursMatch = input.match(/(\d+(?:\.\d+)?)\s*h/i)
  const minutesMatch = input.match(/(\d+)\s*m/i)

  if (hoursMatch) {
    totalMinutes += parseFloat(hoursMatch[1]) * 60
  }
  if (minutesMatch) {
    totalMinutes += parseInt(minutesMatch[1])
  }

  return totalMinutes > 0 ? Math.round(totalMinutes) : null
}

function formatMinutesToInput(minutes: number): string {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  if (h > 0 && m > 0) return `${h}h ${m}m`
  if (h > 0) return `${h}h`
  return `${m}m`
}

export function GoalModal({ activityType, activitySubtype, existingGoal, period, open, onOpenChange }: Props) {
  const { t } = useTranslation()
  const [durationInput, setDurationInput] = useState('')
  const [durationError, setDurationError] = useState<string | null>(null)

  // Get display info based on whether we have a type or subtype
  const activity = activityType || activitySubtype
  const activityName = activitySubtype ? activitySubtype.full_name : activityType?.name || ''
  const activityColor = activitySubtype?.color || activityType?.color || '#888'

  const { data, setData, post, processing, errors, reset } = useForm<GoalFormData>({
    activity_type_id: activityType?.id || null,
    activity_subtype_id: activitySubtype?.id || null,
    period,
    target_minutes: existingGoal?.target_minutes || 0,
  })

  useEffect(() => {
    if (open) {
      setData({
        activity_type_id: activityType?.id || null,
        activity_subtype_id: activitySubtype?.id || null,
        period,
        target_minutes: existingGoal?.target_minutes || 0,
      })
      setDurationInput(existingGoal ? formatMinutesToInput(existingGoal.target_minutes) : '')
      setDurationError(null)
    }
  }, [open, activityType?.id, activitySubtype?.id, period, existingGoal])

  const handleDurationChange = (value: string) => {
    setDurationInput(value)
    const minutes = parseDuration(value)
    if (minutes !== null) {
      setData('target_minutes', minutes)
      setDurationError(null)
    } else if (value.trim()) {
      setDurationError(t('timeEntries.invalidDuration'))
    } else {
      setDurationError(null)
    }
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const minutes = parseDuration(durationInput)
    if (!minutes || minutes <= 0) {
      setDurationError(t('errors.greater_than_zero'))
      return
    }

    post('/goals', {
      preserveScroll: true,
      onSuccess: () => {
        reset()
        onOpenChange(false)
      },
    })
  }

  const handleDelete = () => {
    if (!existingGoal) return

    router.delete(`/goals/${existingGoal.id}`, {
      preserveScroll: true,
      onSuccess: () => {
        onOpenChange(false)
      },
    })
  }

  const periodLabel = period === 'daily' ? t('goals.perDay') : period === 'weekly' ? t('goals.perWeek') : t('goals.perMonth')

  if (!activity) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div
              className="h-4 w-4 rounded"
              style={{ backgroundColor: activityColor }}
            />
            {existingGoal ? t('goals.editGoal') : t('goals.setGoal')}
          </DialogTitle>
          <DialogDescription>
            {t('goals.setGoalFor', { activity: activityName })} ({periodLabel})
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="target">{t('goals.target')}</Label>
            <div className="flex items-center gap-2">
              <Target className="h-4 w-4 text-muted-foreground" />
              <Input
                id="target"
                value={durationInput}
                onChange={(e) => handleDurationChange(e.target.value)}
                placeholder={t('goals.targetPlaceholder')}
                autoFocus
              />
            </div>
            {(durationError || errors.target_minutes) && (
              <p className="text-sm text-destructive">
                {durationError || t(`errors.${errors.target_minutes}`)}
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              {t('goals.targetHint')}
            </p>
          </div>

          <DialogFooter className="flex-col gap-2 sm:flex-row">
            {existingGoal && (
              <Button
                type="button"
                variant="destructive"
                onClick={handleDelete}
                disabled={processing}
                className="w-full sm:w-auto"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                {t('goals.removeGoal')}
              </Button>
            )}
            <div className="flex flex-1 justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {t('common.cancel')}
              </Button>
              <Button type="submit" disabled={processing}>
                {t('common.save')}
              </Button>
            </div>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
